import { t } from '@lingui/core/macro';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { log, useAuthContext } from 'noddi-web';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStartNewBooking } from '../../../hooks/useStartNewBooking';
import { PageHeading } from '../../Layouts/PageHeading';

/**
 * Which prerequisite was absent — for the log only, never shown. The screen cannot name what
 * is missing without lying about where the button goes: `useNewBookingPath()` restarts at the
 * mobile-vs-stationary choice for brands with service centers, and at the address step for the
 * rest, so "tell us your address" would be wrong for half of them.
 */
type MissingBookingDetail = 'address' | 'serviceCenter' | 'car' | 'services' | 'timeSlot' | 'unknown';

type Props = {
  missing?: MissingBookingDetail;
  /** Where Start booking leads. A new booking by default; a booking proposal restarts at its own landing page. */
  onStartOver?: () => void;
};

/**
 * Shown when a step is opened before the steps it depends on — a deep link, a cleared draft, a
 * booking left half-finished, a booking proposal whose chosen time was lost with its tab. Nothing
 * failed, so this is not an error screen: it says the steps are out of order and restarts the flow.
 *
 * Laid out by hand rather than on `NoddiEmptyPage` for the booking-flow shape: filled brand
 * badge, page-level heading, full-width primary action.
 *
 * The body copy keeps an explicit gray: RouterLayout paints this straight onto the brand gradient
 * with no card, where `text-muted-foreground` is only 3.7-4.3:1 -- not AA for body copy.
 */
export const BookingStepUnavailablePage = ({ missing = 'unknown', onStartOver }: Props) => {
  const { isLoggedIn } = useAuthContext();
  const navigate = useNavigate();
  const startNewBooking = useStartNewBooking();
  const title = t`Your booking isn't ready yet`;

  useEffect(() => {
    // debug logs nothing to sentry, not sure if we want to log this somewhere or not, so keeping it as debug for now
    log({ value: `Booking step opened without ${missing}`, severity: 'debug' });
  }, [missing]);

  function startOver() {
    (onStartOver ?? startNewBooking)();
  }

  return (
    <div className='mx-auto flex min-h-[60vh] w-full max-w-md flex-col justify-center gap-6 px-4 py-10 text-center'>
      <div className='flex flex-col items-center gap-5'>
        <div className='flex size-16 items-center justify-center rounded-full bg-primary text-primary-foreground'>
          <NoddiIcon name='XCircle' size='large' />
        </div>
        <div className='flex flex-col gap-2'>
          <PageHeading className='text-2xl font-bold'>{title}</PageHeading>
          <p className='text-gray-600'>
            {t`We couldn't find the rest of your booking details. Starting over only takes a minute.`}
          </p>
        </div>
      </div>

      <div className='flex flex-col items-center gap-4'>
        <NoddiButton onClick={startOver} endIcon='ArrowRight' className='w-full'>
          {t`Start booking`}
        </NoddiButton>
        {/* My page sits behind PrivateRoute — offering it to a guest would just bounce them to login. */}
        {isLoggedIn && (
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
          <NoddiButton variant='link' onClick={() => navigate(customerRoutes.homepage.getPath())}>
            {t`Go to my page`}
          </NoddiButton>
        )}
      </div>
    </div>
  );
};
